import PropTypes from 'prop-types';
import { useState } from 'react';
import { ContainerProps as MuiContainerProps } from '@mui/material/Container';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import { Button, CircularProgress } from '@classity-edtech/ui-base';
import { FullLogo, LegalComponent, Sheet } from '@classity-edtech/ui-base/dist/components/Displays';

import { webPaths } from '../../routes/paths';
import { useGetPageContentQuery } from '../../services/seoContent';
import GoogleButton from '../../components/GoogleButton';

export interface EducatorSignupProps extends MuiContainerProps {}

export type PolicySection = {
  title: string;
  content: {
    subtitle: string;
    content: string[];
    items: string[];
  }[];
};

export const EducatorSignup = ({ className }: EducatorSignupProps) => {
  const navigate = useNavigate();
  const [openSheet, setOpenSheet] = useState(false);
  const [legalType, setLegalType] = useState<'terms' | 'privacy' | ''>('');
  const localisedMessage = (id: string) => {
    const F: any = FormattedMessage;
    return <F id={id} />;
  };

  const { data: privacyPolicy, isLoading: fetchingPrivacyPolicy } = useGetPageContentQuery({
    tag: 'privacyPolicyPage',
    contentType: 'page',
  });

  const { data: termsPolicy, isLoading: fetchingTermsPolicy } = useGetPageContentQuery({
    tag: 'termsOfUsePage',
    contentType: 'page',
  });

  const data =
    legalType === 'privacy' ? privacyPolicy : legalType === 'terms' ? termsPolicy : undefined;

  const handleOpenSheet = (type: 'privacy' | 'terms') => {
    setOpenSheet(true);
    setLegalType(type);
  };

  const handleCloseSheet = () => {
    setOpenSheet(false);
    setLegalType('');
  };

  const extractPolicyHeader = () => {
    const content = data?.sections?.[0];

    return {
      title: content?.fields?.title ?? '',
      effectiveDate: content?.fields?.description?.content?.[0]?.content?.[0]?.value ?? '',
    };
  };

  const extractTextBlocks = description => {
    if (!description?.content) return [];

    return description.content.flatMap(block =>
      (block.content || [])
        .map(item => item?.value)
        .filter(v => typeof v === 'string' && v.trim() !== ''),
    );
  };

  const extractSections = (): PolicySection[] => {
    const sections = data?.sections?.slice(1, -1);

    const sectionMap = {};

    sections?.forEach(section => {
      const sectionTitle = section.fields?.title;
      if (!sectionTitle) return;

      if (!sectionMap[sectionTitle]) {
        sectionMap[sectionTitle] = {
          title: sectionTitle,
          content: [],
        };
      }

      (section.fields?.list || []).forEach(listItem => {
        const fields = listItem?.fields;
        const texts = extractTextBlocks(fields?.description);

        const content = [];
        const items = [];

        texts?.forEach(text => {
          if (text.endsWith(':') || texts?.length === 1) {
            content.push(text);
          } else {
            items.push(text);
          }
        });

        sectionMap[sectionTitle].content.push({
          subtitle: fields?.title || null,
          content,
          items,
        });
      });
    });

    return Object.values(sectionMap);
  };

  const extractContactInformation = () => {
    const content = data?.sections?.[data?.sections?.length - 1];

    return {
      title: content?.fields?.title ?? '',
      description: content?.fields?.description?.content?.[0]?.content?.[0]?.value ?? '',
      contacts:
        content?.fields?.list?.[0]?.fields?.description?.content?.map(
          (block: any) => block?.content?.[0]?.value ?? '',
        ) ?? [],
    };
  };

  return (
    <Box
      className={className}
      sx={{
        width: '100%',
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          rowGap: 0,
          px: { xs: 2.5, md: 4 },
          width: '100%',
          maxWidth: '568px',
        }}
      >
        <Box sx={{ margin: '0 auto', width: 'fit-content' }}>
          <FullLogo
            sx={{
              height: { xs: '28px', md: '42px' },
              width: 'max-content',
              margin: { xs: '0 auto 48px', md: '0 auto 48px' },
              cursor: 'pointer',
            }}
            onClick={() => navigate('/')}
          />
        </Box>
        <Typography variant='subtitle3' sx={{ textAlign: 'center', mx: 'auto', display: 'block' }}>
          {localisedMessage('auth.signup.title')}
        </Typography>
        <Typography
          variant='body2'
          sx={theme => ({
            textAlign: 'center',
            color: globalColours['$global-colour-content-secondary'],
            mb: { xs: 3, md: 6 },
          })}
        >
          {localisedMessage('auth.signup.description')}
        </Typography>

        <GoogleButton text='signin_with' />
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant='body3'>
              {localisedMessage('auth.signup.already.have.account')}
            </Typography>{' '}
            <Button
              variant='text'
              sx={{ display: 'inline', p: 0, minWidth: 'fit-content' }}
              onClick={() => navigate(`/${webPaths.teachSignin}`)}
            >
              {localisedMessage('auth.signin')}
            </Button>
          </Box>
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant='body3'>By signing up, you agree to our</Typography>{' '}
            <Button
              variant='text'
              sx={{
                display: 'inline',
                p: 0,
                '& .MuiTypography-root': {
                  borderBottom: `solid 1px ${globalColours['$global-colour-content-tertiary']}`,
                },
              }}
              onClick={() => handleOpenSheet('privacy')}
            >
              <Typography
                variant='body3SemiBold'
                sx={{
                  color: globalColours['$global-colour-content-primary'],
                }}
              >
                privacy policy
              </Typography>
            </Button>{' '}
            <Typography variant='body3'>and</Typography>{' '}
            <Button
              variant='text'
              sx={{
                display: 'inline',
                p: 0,
                '& .MuiTypography-root': {
                  borderBottom: `solid 1px ${globalColours['$global-colour-content-tertiary']}`,
                },
              }}
              onClick={() => handleOpenSheet('terms')}
            >
              <Typography
                variant='body3SemiBold'
                sx={{
                  color: globalColours['$global-colour-content-primary'],
                }}
              >
                terms of use
              </Typography>
            </Button>
          </Box>

          {openSheet && (
            <Box
              sx={{
                position: 'fixed',
                inset: 0,
                zIndex: 5,
                backgroundColor: globalColours['$global-colour-background-dimmer'],
              }}
              onClick={handleCloseSheet}
            ></Box>
          )}

          <Sheet
            closeSheet={handleCloseSheet}
            openSheet={openSheet}
            body={
              fetchingPrivacyPolicy || fetchingTermsPolicy ? (
                <Box>
                  <CircularProgress />
                </Box>
              ) : (
                <LegalComponent
                  header={extractPolicyHeader()}
                  sections={extractSections()}
                  contacts={extractContactInformation()}
                />
              )
            }
          />
        </Box>
      </Box>
    </Box>
  );
};

EducatorSignup.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

EducatorSignup.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(EducatorSignup)(({ theme }) => ``);
